import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import { API_BASE } from './config';
import { createMonitorSocket } from '../socket/socket';
import BottomNavigation from './BottomNavigation';

import '../css/Monitor.css';


/* =================================================
   HELPERS
================================================= */

const toArray = value => (
  Array.isArray(value)
    ? value
    : Array.isArray(value?.data)
      ? value.data
      : []
);

const urlBase64ToUint8Array = value => {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(character => character.charCodeAt(0)));
};


const fetchJson = async path => {

  const response = await fetch(
    `${API_BASE}${path}`
  );

  const data = await response
    .json()
    .catch(() => ({}));

  if (!response.ok) {

    throw new Error(
      data.message ||
      data.error ||
      `HTTP ${response.status}`
    );

  }

  return data;
};


/* =================================================
   MATCH TIME
================================================= */

const matchTimestamp = match => {

  const timestamp =
    new Date(
      match?.created_at || 0
    ).getTime();

  return Number.isFinite(timestamp)
    ? timestamp
    : 0;
};


const endedMatchTimestamp = match => {

  const timestamp =
    new Date(
      match?.ended_at ||
      match?.end_time ||
      match?.updated_at ||
      match?.created_at ||
      0
    ).getTime();

  return Number.isFinite(timestamp)
    ? timestamp
    : 0;
};

const formatDuration = milliseconds => {
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .filter((_, index) => hours > 0 || index > 0)
    .map(value => String(value).padStart(2, '0'))
    .join(':');
};

const matchResultLabel = result => ({
  team1_win: '🏆 Team 1 ชนะ',
  team2_win: '🏆 Team 2 ชนะ',
  draw: '🤝 เสมอ'
}[String(result || '').toLowerCase()] || 'ยังไม่ระบุผล');

/* =================================================
   PLAYER COLOR
================================================= */

const playerColorStyle = player => {

  const source =
    String(
      player?.user_id ??
      player?.name ??
      'player'
    );

  const hash =
    [...source].reduce(
      (total, character) =>
        (
          (
            total * 31
          ) +
          character.charCodeAt(0)
        ) >>> 0,
      0
    );

  const hue =
    Math.round(
      (hash * 137.508) % 360
    );

  return {
    '--monitor-player-ink':
      `hsl(${hue} 62% 31%)`,

    '--monitor-player-bg':
      `hsl(${hue} 86% 95%)`,

    '--monitor-player-border':
      `hsl(${hue} 62% 82%)`
  };
};


/* =================================================
   AVATAR URL
================================================= */

const getAvatarSource = avatarUrl => {

  if (!avatarUrl) {
    return '';
  }

  if (
    /^https?:\/\//i.test(avatarUrl) ||
    avatarUrl.startsWith('data:')
  ) {
    return avatarUrl;
  }

  return (
    `${API_BASE}` +
    `${avatarUrl.startsWith('/') ? '' : '/'}` +
    `${avatarUrl}`
  );
};


/* =================================================
   PLAYER BUTTON
================================================= */

function PlayerNameButton({
  player,
  onClick
}) {

  const playerName =
    player?.name ||
    'ไม่ระบุชื่อ';


  /*
   * รองรับหลายชื่อ field
   * เผื่อ Backend ส่งต่างกัน
   */
  const avatarUrl =
    player?.avatarUrl ||
    player?.avatar_url ||
    player?.avatar ||
    '';


  const firstLetter =
    playerName
      .trim()
      .charAt(0)
      .toUpperCase() ||
    '🏸';


  return (

    <button
      type="button"

      className="monitor-player-link"

      style={
        playerColorStyle(player)
      }

      onClick={() =>
        onClick(player)
      }

      title={
        `ดูประวัติการเล่นของ ${playerName}`
      }
    >

      {/* ================= AVATAR ================= */}

      <span className="monitor-player-avatar">

        {avatarUrl ? (

          <img
            src={
              getAvatarSource(
                avatarUrl
              )
            }

            alt={
              `รูปของ ${playerName}`
            }

            onError={event => {

              event.currentTarget.style.display =
                'none';

              const fallback =
                event.currentTarget
                  .nextElementSibling;

              if (fallback) {

                fallback.style.display =
                  'grid';

              }

            }}
          />

        ) : null}


        <span
          className="monitor-player-avatar-fallback"

          style={{
            display:
              avatarUrl
                ? 'none'
                : 'grid'
          }}
        >
          {firstLetter}
        </span>

      </span>


      {/* ================= NAME ================= */}

      <span className="monitor-player-copy">
        <span className="monitor-player-name">
          {playerName}
        </span>

        {player?.playingOtherMatchNo && (
          <small className="monitor-player-busy">
            รอผู้เล่น • Match #{player.playingOtherMatchNo}
            {player.playingOtherCourtNo ? ` • Court ${player.playingOtherCourtNo}` : ''}
          </small>
        )}
      </span>

    </button>

  );

}


/* =================================================
   MATCH CARD
================================================= */

function MatchCard({
  match,
  tone,
  onPlayerClick,
  now,
  onEditResult,
  isSubmitting,
  isAdmin
}) {

  const players =
    Array.isArray(match?.players)
      ? match.players
      : [];


  const team1 =
    players.filter(
      player =>
        Number(player.team) === 1
    );


  const team2 =
    players.filter(
      player =>
        Number(player.team) === 2
    );


  const courtLabel =
    match.court_no
      ? `Court ${match.court_no}`
      : '-';

  const startTimestamp = new Date(match.start_time || match.created_at || 0).getTime();
  const endTimestamp = endedMatchTimestamp(match);
  const duration = startTimestamp > 0
    ? formatDuration((tone === 'ended' ? endTimestamp : now) - startTimestamp)
    : '--:--';
  const normalizedResult = String(match.result || '').toLowerCase();
  const editWindowOpen =
    isAdmin ||
    (endTimestamp > 0 && now - endTimestamp <= 5 * 60 * 1000);
  const team1Won = tone === 'ended' && normalizedResult === 'team1_win';
  const team2Won = tone === 'ended' && normalizedResult === 'team2_win';
  const isDraw = tone === 'ended' && normalizedResult === 'draw';


  return (

    <article
      className={
        `monitor-match-card ${tone}${team1Won ? ' team1-winner' : ''}${team2Won ? ' team2-winner' : ''}${isDraw ? ' match-draw' : ''}`
      }
    >

      {/* ================= HEADER ================= */}

      <header>

        <span>

          Match #

          {
            match.groupMatchNo ||
            match.matchId
          }

        </span>


        <strong className={tone === 'playing' ? 'monitor-court-badge' : ''}>

          {
            tone === 'playing'
              ? courtLabel

              : tone === 'ended'
                ? `✅ จบแล้ว • ${courtLabel}`

                : 'รอสนาม'
          }

        </strong>

      </header>


      {/* ================= TEAM ================= */}

      <div className="monitor-match-teams">


        {/* ================= TEAM 1 ================= */}

        <div className={team1Won ? 'winner-team' : team2Won ? 'loser-team' : ''}>

          <small>
            T1
          </small>


          {team1.map(player => (

            <PlayerNameButton
              key={
                player.user_id
              }

              player={
                player
              }

              onClick={
                onPlayerClick
              }
            />

          ))}

        </div>


        {/* ================= VS ================= */}

        <b>
          VS
        </b>


        {/* ================= TEAM 2 ================= */}

        <div className={team2Won ? 'winner-team' : team1Won ? 'loser-team' : ''}>

          <small>
            T2
          </small>


          {team2.map(player => (

            <PlayerNameButton
              key={
                player.user_id
              }

              player={
                player
              }

              onClick={
                onPlayerClick
              }
            />

          ))}

        </div>

      </div>

      {tone === 'playing' && (
        <div className="monitor-playing-duration" aria-label="เวลาแข่งขัน">
          <span className="monitor-match-duration">⏱ {duration}</span>
        </div>
      )}

      {tone === 'ended' && (
        <div className="monitor-ended-summary">
          <span><b>{matchResultLabel(match.result)}</b> • เวลาเล่น {duration}</span>
          <div className="monitor-result-actions" aria-label="เลือกผล Match ที่จบแล้ว">
            <button className={normalizedResult === 'team1_win' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'TEAM1')}>T1 Win</button>
            <button className={normalizedResult === 'draw' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'DRAW')}>Draw</button>
            <button className={normalizedResult === 'team2_win' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'TEAM2')}>T2 Win</button>
          </div>
          {!editWindowOpen && (
            <small className="monitor-result-locked">🔒 เกิน 5 นาที เฉพาะ Admin เท่านั้นที่แก้ผลได้</small>
          )}
          {isAdmin && endTimestamp > 0 && now - endTimestamp > 5 * 60 * 1000 && (
            <small className="monitor-result-admin">🛡 Admin แก้ผลย้อนหลังได้</small>
          )}
        </div>
      )}

    </article>

  );

}


/* =================================================
   MATCH BOARD
================================================= */

function MatchBoard({
  matches,
  onPlayerClick,
  now,
  onEditResult,
  submittingMatchId,
  isAdmin
}) {


  /* ================= PLAYING ================= */

  const playingMatches =
    useMemo(
      () =>
        matches
          .filter(
            match =>
              String(
                match.status
              ).toLowerCase() ===
              'playing'
          )
          .sort(
            (left, right) =>
              Number(
                left.court_no ||
                999
              ) -
              Number(
                right.court_no ||
                999
              )
          ),

      [matches]
    );


  /* ================= QUEUE ================= */

  const queueMatches =
    useMemo(
      () =>
        matches
          .filter(
            match =>
              [
                'waiting',
                'waiting_queue'
              ].includes(
                String(
                  match.status
                ).toLowerCase()
              )
          )
          .sort(
            (left, right) =>

              Number(
                left.queue_no ||
                999999
              ) -

                Number(
                  right.queue_no ||
                  999999
                )

              ||

              matchTimestamp(
                left
              ) -

                matchTimestamp(
                  right
                )

              ||

              Number(
                left.matchId
              ) -

                Number(
                  right.matchId
                )
          ),

      [matches]
    );


  /* ================= ENDED ================= */

  const endedMatches =
    useMemo(
      () =>
        matches
          .filter(
            match =>
              [
                'ended',
                'end',
                'completed',
                'finished'
              ].includes(
                String(
                  match.status || ''
                )
                  .trim()
                  .toLowerCase()
              )
          )
          .sort(
            (left, right) =>

              endedMatchTimestamp(
                right
              ) -

                endedMatchTimestamp(
                  left
                )

              ||

              Number(
                right.matchId ||
                0
              ) -

                Number(
                  left.matchId ||
                  0
                )
          ),

      [matches]
    );


  /* =================================================
     RENDER
  ================================================= */

  return (

    <>

      {/* =================================================
         QUEUE + PLAY
      ================================================= */}

      <section
        className="monitor-match-board"
        aria-label="Match Play และ Match Queue"
      >


        {/* ================= QUEUE ================= */}

        <div className="monitor-match-column">

          <header
            className="
              monitor-match-heading
              queue-heading
            "
          >

            <span>

              <strong>
                Match Queue
              </strong>

              <small>
                Match ที่กำลังรอลงสนามตามลำดับคิว
              </small>

            </span>


            <b>
              {queueMatches.length} Match
            </b>

          </header>


          <div className="monitor-match-list">

            {
              queueMatches.length > 0

                ? queueMatches.map(
                    match => (

                      <MatchCard
                        key={
                          match.matchId
                        }

                        match={
                          match
                        }

                        tone="waiting"

                        onPlayerClick={
                          onPlayerClick
                        }
                        now={now}
                      />

                    )
                  )

                : (

                  <div className="monitor-empty">

                    ยังไม่มี Match ในคิว

                  </div>

                )
            }

          </div>

        </div>


        {/* ================= PLAY ================= */}

        <div className="monitor-match-column">

          <header
            className="
              monitor-match-heading
              play-heading
            "
          >

            <span>

              <strong>
                Match Play
              </strong>

              <small>
                Match ที่กำลังแข่งขันอยู่
              </small>

            </span>


            <b>
              {playingMatches.length} Match
            </b>

          </header>


          <div className="monitor-match-list">

            {
              playingMatches.length > 0

                ? playingMatches.map(
                    match => (

                      <MatchCard
                        key={
                          match.matchId
                        }

                        match={
                          match
                        }

                        tone="playing"

                        onPlayerClick={
                          onPlayerClick
                        }
                        now={now}
                      />

                    )
                  )

                : (

                  <div className="monitor-empty">

                    ยังไม่มี Match ที่กำลังเล่น

                  </div>

                )
            }

          </div>

        </div>

      </section>


      {/* =================================================
         ENDED
      ================================================= */}

      <section className="monitor-ended-section">

        <header
          className="
            monitor-match-heading
            ended-heading
          "
        >

          <span>

            <strong>
              ✅ Match จบแล้ว
            </strong>

            <small>
              ประวัติ Match ที่เล่นจบแล้ว
            </small>

          </span>


          <b>
            {endedMatches.length} Match
          </b>

        </header>


        <div className="monitor-ended-list">

          {
            endedMatches.length > 0

              ? endedMatches.map(
                  match => (

                    <MatchCard
                      key={
                        match.matchId
                      }

                      match={
                        match
                      }

                      tone="ended"

                      onPlayerClick={
                        onPlayerClick
                      }
                      now={now}
                      onEditResult={onEditResult}
                      isSubmitting={Number(submittingMatchId) === Number(match.matchId)}
                      isAdmin={isAdmin}
                    />

                  )
                )

              : (

                <div className="monitor-empty">

                  ยังไม่มี Match ที่จบแล้ว

                </div>

              )
          }

        </div>

      </section>

    </>

  );

}


/* =================================================
   MONITOR
================================================= */

function Monitor() {

  /* ================= ROUTER ================= */

  const {
    groupId: routeGroupId
  } = useParams();


  const navigate =
    useNavigate();


  const [selectedGroupId, setSelectedGroupId] = useState(() =>
    Number(routeGroupId) || Number(localStorage.getItem('last_group_id')) || null
  );

  const loggedInUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (selectedGroupId) localStorage.setItem('last_group_id', String(selectedGroupId));
  }, [selectedGroupId]);

  // Keep old shared links working, but expose the clean canonical URL.
  useEffect(() => {
    if (routeGroupId) navigate('/monitor', { replace: true });
  }, [navigate, routeGroupId]);


  /* ================= STATE ================= */

  const [groups, setGroups] =
    useState([]);

  const [matches, setMatches] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState('');

  const [now, setNow] = useState(() => Date.now());
  const [submittingMatchId, setSubmittingMatchId] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [, setMyGroupStatus] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [onlineViewerCount, setOnlineViewerCount] = useState(0);
  const [inAppAlert, setInAppAlert] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [showIosInstallPrompt, setShowIosInstallPrompt] = useState(false);
  const audioContextRef = useRef(null);
  const notifiedMatchStatesRef = useRef(new Map());


  const requestVersionRef =
    useRef(0);

  const unlockAlertAudio = useCallback(() => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioContextRef.current) audioContextRef.current = new AudioContextClass();
    if (audioContextRef.current.state === 'suspended') audioContextRef.current.resume().catch(() => {});
    return audioContextRef.current;
  }, []);

  const ringAlertBell = useCallback(() => {
    const context = unlockAlertAudio();
    if (!context || context.state !== 'running') return;
    const playTone = (frequency, delay, duration, volume) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, context.currentTime + delay);
      gain.gain.setValueAtTime(0.0001, context.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(volume, context.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + delay + duration);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(context.currentTime + delay);
      oscillator.stop(context.currentTime + delay + duration + 0.03);
    };
    playTone(880, 0, 0.34, 0.24);
    playTone(1175, 0.2, 0.48, 0.2);
    playTone(880, 0.72, 0.34, 0.2);
    playTone(1175, 0.92, 0.55, 0.18);
  }, [unlockAlertAudio]);

  const showMatchAlert = useCallback((match, status) => {
    const matchNo = match.group_match_no || match.groupMatchNo || match.id;
    const isPlaying = status === 'PLAYING';
    setInAppAlert({
      type: isPlaying ? 'PLAY' : 'QUEUE',
      title: isPlaying ? '📣 ถึงเวลาลงสนาม' : '🏸 ใกล้ถึงคิวของคุณแล้ว',
      body: isPlaying
        ? `Match #${matchNo} • Court ${match.court_no || match.courtNo || '-'}`
        : `Match #${matchNo} อยู่ในคิวแล้วกรุณาเตรียมตัว`
    });
    ringAlertBell();
    if ('vibrate' in navigator) navigator.vibrate(isPlaying ? [350, 120, 350, 120, 650] : [250, 100, 250]);
  }, [ringAlertBell]);

  useEffect(() => {
    const unlockOnce = () => unlockAlertAudio();
    window.addEventListener('pointerdown', unlockOnce, { once: true });
    window.addEventListener('keydown', unlockOnce, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockOnce);
      window.removeEventListener('keydown', unlockOnce);
    };
  }, [unlockAlertAudio]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (loading || !loggedInUser?.user_id) return;
    const userId = Number(loggedInUser.user_id);
    const current = new Map();
    const relevant = matches.filter(match =>
      (Array.isArray(match.players) ? match.players : []).some(player => Number(player.user_id) === userId)
    );

    relevant.forEach(match => {
      const status = String(match.status || '').trim().toUpperCase();
      const normalized = status === 'PLAYING'
        ? 'PLAYING'
        : ['WAITING', 'WAITING_QUEUE', 'QUEUE'].includes(status) ? 'QUEUE' : '';
      if (!normalized) return;
      const matchId = Number(match.id || match.matchId);
      current.set(matchId, normalized);
      const previous = notifiedMatchStatesRef.current.get(matchId);
      if (document.visibilityState === 'visible' && previous !== normalized) showMatchAlert(match, normalized);
    });

    notifiedMatchStatesRef.current = current;
  }, [loading, loggedInUser?.user_id, matches, showMatchAlert]);

  useEffect(() => {
    if (!inAppAlert) return undefined;
    const timer = window.setTimeout(() => setInAppAlert(null), 15000);
    return () => window.clearTimeout(timer);
  }, [inAppAlert]);

  useEffect(() => {
    if (!loggedInUser?.user_id) return;
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isIos && !isStandalone) {
      if (sessionStorage.getItem('ios_install_prompt_dismissed') !== '1') setShowIosInstallPrompt(true);
      return;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    let cancelled = false;
    navigator.serviceWorker.register('/push-sw.js')
      .then(registration => registration.pushManager.getSubscription())
      .then(subscription => {
        if (cancelled) return;
        const enabled = Boolean(subscription);
        setPushEnabled(enabled);
        if (
          !enabled &&
          Notification.permission !== 'denied' &&
          sessionStorage.getItem('monitor_push_prompt_dismissed') !== '1'
        ) setShowPushPrompt(true);
      })
      .catch(() => { if (!cancelled) setPushEnabled(false); });
    return () => { cancelled = true; };
  }, [loggedInUser?.user_id]);

  useEffect(() => {
    if (!loggedInUser?.user_id || !selectedGroupId) return;
    fetch(`${API_BASE}/api/checkin/status/${loggedInUser.user_id}`)
      .then(response => response.json())
      .then(data => {
        if (Number(data.group_id) === Number(selectedGroupId)) setMyGroupStatus(String(data.status || '').toUpperCase());
      })
      .catch(() => {});
  }, [loggedInUser?.user_id, selectedGroupId]);


  /* =================================================
     LOAD GROUP
  ================================================= */

  const loadGroups =
    useCallback(
      async () => {

        const data =
          await fetchJson(
            '/api/groups'
          );

        const nextGroups = toArray(data).filter(group =>
          !['CLOSE', 'CLOSED'].includes(
            String(group.Group_Status || '').trim().toUpperCase()
          )
        );

        setGroups(
          nextGroups
        );

        return nextGroups;

      },
      []
    );


  /* =================================================
     LOAD MATCHES
  ================================================= */

  const loadMatches =
    useCallback(
      async ({
        silent = false
      } = {}) => {

        if (
          !selectedGroupId
        ) {

          setMatches([]);

          setLoading(false);

          return;

        }


        const requestVersion =
          ++requestVersionRef.current;


        if (silent) {

          setRefreshing(true);

        } else {

          setLoading(true);

        }


        try {

          const data =
            await fetchJson(
              `/api/game-match/group/${selectedGroupId}`
            );


          if (
            requestVersion !==
            requestVersionRef.current
          ) {

            return;

          }


          setMatches(
            toArray(data)
          );


          setError('');


        } catch (loadError) {

          if (
            requestVersion !==
            requestVersionRef.current
          ) {

            return;

          }


          console.error(
            '[MONITOR MATCH]',
            loadError
          );


          setError(
            loadError.message ||
            'โหลด Match Monitor ไม่สำเร็จ'
          );


        } finally {

          if (
            requestVersion ===
            requestVersionRef.current
          ) {

            setLoading(false);

            setRefreshing(false);

          }

        }

      },

      [selectedGroupId]
    );


  /* =================================================
     LOAD GROUP
  ================================================= */

  useEffect(() => {

    loadGroups()
      .catch(
        loadError => {

          setError(
            loadError.message ||
            'โหลดรายการ Group ไม่สำเร็จ'
          );

          setLoading(false);

        }
      );

  }, [loadGroups]);

  // Keep the waiting screen current even if a production proxy temporarily
  // misses a Socket.IO group event.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadGroups().catch(() => {});
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [loadGroups]);


  /* =================================================
     VALIDATE GROUP
  ================================================= */

  useEffect(() => {

    if (
      groups.length === 0
    ) {
      setSelectedGroupId(null);
      return;
    }


    const selectedExists =
      groups.some(
        group =>
          Number(group.id) ===
          Number(selectedGroupId)
      );


    if (
      !selectedExists
    ) {

      setSelectedGroupId(Number(groups[0].id));

    }

  }, [
    groups,
    selectedGroupId
  ]);


  /* =================================================
     LOAD MATCH
  ================================================= */

  useEffect(() => {

    loadMatches();

  }, [loadMatches]);


  /* =================================================
     FALLBACK 30 SEC
  ================================================= */

  useEffect(() => {

    if (
      !selectedGroupId
    ) {
      return undefined;
    }


    const intervalId =
      window.setInterval(
        () =>
          loadMatches({
            silent: true
          }),

        30000
      );


    return () => {

      window.clearInterval(
        intervalId
      );

    };

  }, [
    loadMatches,
    selectedGroupId
  ]);


  /* =================================================
     SOCKET
  ================================================= */

  useEffect(() => {
    const monitorSocket =
      createMonitorSocket();


    const joinGroup = () => {

      monitorSocket.emit('join-monitor-view');

      if (!selectedGroupId) return;

      monitorSocket.emit(
        'join-group',
        selectedGroupId
      );

    };


    const refreshMatches =
      (payload = {}) => {

        const eventGroupId =
          payload.groupId ??
          payload.group_id;


        if (
          eventGroupId &&
          Number(eventGroupId) !==
            selectedGroupId
        ) {

          return;

        }


        loadMatches({
          silent: true
        });

      };


    const matchEvents = [

      'match-generated',

      'match-started',

      'match-ended',

      'match-result-updated',

      'match-updated',

      'queue-updated',

      'match-cancelled',

      'court-status-updated',

      // เพิ่มไว้เผื่อ avatar update
      'player-avatar-updated'

    ];


    monitorSocket.on(
      'connect',
      joinGroup
    );

    const updateViewerCount = payload => {
      setOnlineViewerCount(Math.max(0, Number(payload?.count || 0)));
    };
    monitorSocket.on('monitor-viewers-updated', updateViewerCount);


    matchEvents.forEach(
      eventName =>

        monitorSocket.on(
          eventName,
          refreshMatches
        )
    );

    const refreshGroups = () => loadGroups().catch(() => {});
    const groupEvents = [
      'group-created',
      'group-opened',
      'group-closed',
      'group-deleted'
    ];
    groupEvents.forEach(eventName => {
      monitorSocket.on(eventName, refreshGroups);
    });


    if (
      monitorSocket.connected
    ) {

      joinGroup();

    }


    return () => {

      monitorSocket.off(
        'connect',
        joinGroup
      );

      monitorSocket.off('monitor-viewers-updated', updateViewerCount);


      matchEvents.forEach(
        eventName =>

          monitorSocket.off(
            eventName,
            refreshMatches
          )
      );

      groupEvents.forEach(eventName => {
        monitorSocket.off(eventName, refreshGroups);
      });


      monitorSocket.disconnect();

    };

  }, [
    loadGroups,
    loadMatches,
    selectedGroupId
  ]);


  /* =================================================
     SELECT GROUP
  ================================================= */

  const selectedGroup =
    groups.find(
      group =>
        Number(group.id) ===
        Number(selectedGroupId)
    );

  const submitMatchResult = useCallback(async (match, winnerTeam, editing = false) => {
    if (!match?.matchId || submittingMatchId !== null) return;

    const action = editing ? 'แก้ไขผล' : 'จบ Match';

    try {
      setSubmittingMatchId(match.matchId);
      const response = await fetch(
        editing
          ? `${API_BASE}/api/game-match/${match.matchId}/result`
          : `${API_BASE}/api/game-match/end`,
        {
          method: editing ? 'PATCH' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token')
              ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
              : {})
          },
          body: JSON.stringify({ matchId: match.matchId, winnerTeam })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || `${action}ไม่สำเร็จ`);
      await loadMatches({ silent: true });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmittingMatchId(null);
    }
  }, [loadMatches, submittingMatchId]);


  const handleGroupChange =
    event => {

      const nextGroupId =
        Number(
          event.target.value
        );


      if (
        nextGroupId
      ) {

        setSelectedGroupId(nextGroupId);

      }

    };


  /* =================================================
     PLAYER HISTORY
  ================================================= */

  const openPlayerHistory =
    player => {

      if (
        !player?.user_id ||
        !selectedGroupId
      ) {

        return;

      }


      navigate(
        `/player-history/1/${player.user_id}`
      );

    };


  /* =================================================
     RANK
  ================================================= */

  const openRankTable =
    () => {

      navigate(
        '/rank/'
      );

    };

  const openMyProfile = () => {
    const userId = Number(loggedInUser?.user_id);
    if (!userId) return;
    navigate('/profile');
  };

  const openNotificationLogin = () => {
    const returnPath = '/monitor';
    navigate(`/?redirect=${encodeURIComponent(returnPath)}`);
  };

  const togglePushNotifications = async () => {
    if (pushBusy) return;
    const token = localStorage.getItem('device_token') || localStorage.getItem('token');
    if (!token) {
      setPushMessage('กรุณา Login ก่อนเปิดแจ้งเตือน');
      return;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setPushMessage('Browser เครื่องนี้ยังไม่รองรับ Web Push');
      return;
    }
    try {
      setPushBusy(true);
      setPushMessage('');
      const registration = await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      if (pushEnabled || current) {
        await fetch(`${API_BASE}/api/push/unsubscribe`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ endpoint: current?.endpoint })
        });
        if (current) await current.unsubscribe();
        setPushEnabled(false);
        setPushMessage('ปิดแจ้งเตือนแล้ว');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error('กรุณาอนุญาต Notification ใน Browser');
      const keyResponse = await fetch(`${API_BASE}/api/push/public-key`);
      const keyData = await keyResponse.json().catch(() => ({}));
      if (!keyResponse.ok) throw new Error(keyData.message || 'โหลด Push Key ไม่สำเร็จ');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyData.publicKey)
      });
      const response = await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'เปิดแจ้งเตือนไม่สำเร็จ');
      setPushEnabled(true);
      setShowPushPrompt(false);
      setPushMessage('✓ เปิดแจ้งเตือนแล้ว');
      await fetch(`${API_BASE}/api/push/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: '/monitor' })
      });
    } catch (pushError) {
      setPushMessage(pushError.message);
    } finally {
      setPushBusy(false);
    }
  };

  const logoutMyself = async () => {
    if (checkingOut) return;
    const token = localStorage.getItem('device_token') || localStorage.getItem('token');
    if (!token) {
      setCheckoutMessage('กรุณา Login ก่อน Logout');
      return;
    }
    if (!window.confirm('Logout ใช่หรือไม่?\nสถานะของคุณในทุก Group จะเปลี่ยนเป็น HOLD')) return;
    try {
      setCheckingOut(true);
      setCheckoutMessage('');
      const response = await fetch(`${API_BASE}/api/checkin/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Logout ไม่สำเร็จ');
      localStorage.removeItem('token');
      localStorage.removeItem('device_token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('loginRedirect');
      setMyGroupStatus('HOLD');
      setCheckoutMessage('✓ Logout แล้ว • ทุก Group เปลี่ยนเป็น HOLD');
    } catch (checkoutError) {
      setCheckoutMessage(checkoutError.message);
    } finally {
      setCheckingOut(false);
    }
  };


  /* =================================================
     RENDER
  ================================================= */

  return (

    <main className="monitor-page monitor-match-only">

      {showIosInstallPrompt && (
        <div className="monitor-ios-install" role="dialog" aria-modal="true" aria-labelledby="monitor-ios-install-title">
          <button
            type="button"
            className="monitor-ios-install-close"
            onClick={() => {
              sessionStorage.setItem('ios_install_prompt_dismissed', '1');
              setShowIosInstallPrompt(false);
            }}
            aria-label="ปิด"
          >×</button>
          <div className="monitor-ios-install-icon">📲</div>
          <strong id="monitor-ios-install-title">เปิดแจ้งเตือนบน iPhone</strong>
          <p>iOS ต้องเปิดเว็บจากไอคอนบนหน้าจอโฮมก่อน จึงจะรับแจ้งเตือน Match ได้</p>
          <ol>
            <li>กดปุ่ม Share <b>□↑</b> ด้านล่าง Safari</li>
            <li>เลือก <b>Add to Home Screen</b></li>
            <li>เปิดไอคอน Badminton จากหน้าจอโฮม</li>
            <li>กด <b>เปิดแจ้งเตือน</b> ในหน้า Monitor อีกครั้ง</li>
          </ol>
          <small>รองรับ iOS/iPadOS 16.4 ขึ้นไป และต้องใช้ HTTPS</small>
        </div>
      )}

      {showPushPrompt && (
        <div className="monitor-push-prompt" role="dialog" aria-modal="true" aria-labelledby="monitor-push-prompt-title">
          <div className="monitor-push-prompt-icon">🔔</div>
          <strong id="monitor-push-prompt-title">เปิดแจ้งเตือน Match แบบเรียลไทม์</strong>
          <p>รับแจ้งเตือนเมื่อถึง Queue และเมื่อถึงเวลาลงสนาม แม้ไม่ได้มองหน้าจออยู่</p>
          <div>
            <button
              type="button"
              className="monitor-push-prompt-later"
              onClick={() => {
                sessionStorage.setItem('monitor_push_prompt_dismissed', '1');
                setShowPushPrompt(false);
              }}
            >
              ไว้ภายหลัง
            </button>
            <button
              type="button"
              className="monitor-push-prompt-enable"
              disabled={pushBusy}
              onClick={togglePushNotifications}
            >
              {pushBusy ? 'กำลังเปิด…' : '🔔 เปิดแจ้งเตือน'}
            </button>
          </div>
          {pushMessage && <small>{pushMessage}</small>}
        </div>
      )}

      {inAppAlert && (
        <div className={`monitor-inapp-alert monitor-inapp-${inAppAlert.type.toLowerCase()}`} role="alert" aria-live="assertive">
          <button type="button" className="monitor-inapp-close" onClick={() => setInAppAlert(null)} aria-label="ปิดแจ้งเตือน">×</button>
          <div className="monitor-inapp-bell">{inAppAlert.type === 'PLAY' ? '📣' : '🔔'}</div>
          <strong>{inAppAlert.title}</strong>
          <span>{inAppAlert.body}</span>
          <button type="button" className="monitor-inapp-confirm" onClick={() => setInAppAlert(null)}>รับทราบ</button>
        </div>
      )}


      {/* =================================================
         HERO
      ================================================= */}

      <header className="monitor-hero">


        <div className="monitor-brand">

          <span
            className="monitor-live-dot"
            aria-hidden="true"
          />


          <div>

            <span className="monitor-eyebrow">

              LIVE MATCH MONITOR

            </span>


            <h1>

              🏸{' '}

              {
                selectedGroup?.Group_Name ||
                'Match Monitor'
              }

            </h1>


            <p>

              แสดง Match Play และ Match Queue แบบเรียลไทม์

            </p>

            <span className="monitor-online-viewers">👁 Online {onlineViewerCount} คน</span>

          </div>

        </div>


        {/* ================= RANK ================= */}

        <button
          type="button"

          className="monitor-rank-button"

          onClick={
            openRankTable
          }
        >

          🏆 ดูอันดับคะแนน

        </button>

        {!loggedInUser?.user_id && (
          <section className="monitor-notification-login">
            <span>🔔</span>
            <div>
              <strong>การแจ้งเตือนแมท</strong>
              <small>Login เพื่อรับแจ้งเตือนเมื่อถึง Queue และลงสนาม</small>
            </div>
            <button type="button" onClick={openNotificationLogin}>Login</button>
          </section>
        )}

        {loggedInUser?.user_id && (
          <div className="monitor-member-actions">
            <button type="button" className="monitor-profile-button" onClick={openMyProfile}>
              🔍 {loggedInUser.name || `Member #${loggedInUser.user_id}`}
            </button>
            <button type="button" className="monitor-checkout-button" disabled={checkingOut} onClick={logoutMyself}>
              {checkingOut ? '…' : '🚪 Logout'}
            </button>
            <button
              type="button"
              className={`monitor-push-button ${pushEnabled ? 'is-enabled' : ''}`}
              disabled={pushBusy}
              onClick={togglePushNotifications}
            >
              {pushBusy ? '…' : pushEnabled ? '🔔 แจ้งเตือน: เปิด' : '🔕 เปิดแจ้งเตือน'}
            </button>
          </div>
        )}

        {checkoutMessage && <small className="monitor-checkout-message">{checkoutMessage}</small>}
        {pushMessage && <small className="monitor-push-message">{pushMessage}</small>}


        {/* ================= TOOLBAR ================= */}

        <div className="monitor-toolbar">


          <label htmlFor="monitor-group-select">

            <span>
              เลือก Group
            </span>


            <select
              id="monitor-group-select"

              value={
                selectedGroupId ||
                ''
              }

              onChange={
                handleGroupChange
              }

              disabled={
                groups.length === 0
              }
            >

              {groups.length === 0 && (

                <option value="">

                  ไม่พบ Group

                </option>

              )}


              {groups.map(
                group => (

                  <option
                    value={
                      group.id
                    }

                    key={
                      group.id
                    }
                  >

                    {
                      group.Group_Name
                    }

                    {' ('}

                    {
                      group.Group_Status ||
                      'OPEN'
                    }

                    {')'}

                  </option>

                )
              )}

            </select>

          </label>


          <button
            type="button"

            className="monitor-refresh-button"

            disabled={
              !selectedGroupId ||
              refreshing
            }

            onClick={() =>
              loadMatches({
                silent: true
              })
            }
          >

            {
              refreshing

                ? '⏳ กำลังอัปเดต'

                : '↻ อัปเดตข้อมูล'
            }

          </button>

        </div>

      </header>


      {/* =================================================
         ERROR
      ================================================= */}

      {error && (

        <div
          className="monitor-error"
          role="alert"
        >

          ❌ {error}

        </div>

      )}


      {/* =================================================
         CONTENT
      ================================================= */}

      {loading ? (

        <div className="monitor-loading">

          กำลังโหลด Match Monitor...

        </div>

      ) : groups.length === 0 ? (

        <section className="monitor-no-open-group" role="status">
          <div aria-hidden="true">🏸</div>
          <h2>ยังไม่มี Group เปิดอยู่</h2>
          <p>ก๊วนน้ามาดเริ่ม 19.00–00.00</p>
          <small>จัดทุกวันอังคารและวันพฤหัสบดี • เปิด 10 สนาม</small>
          <strong>แล้วพบกันนะครับ</strong>
        </section>

      ) : (

        <MatchBoard
          matches={
            matches
          }

          onPlayerClick={
            openPlayerHistory
          }
          now={now}
          submittingMatchId={submittingMatchId}
          isAdmin={String(loggedInUser?.role || '').toLowerCase() === 'admin'}
          onEditResult={(match, winnerTeam) => submitMatchResult(match, winnerTeam, true)}
        />

      )}

      <BottomNavigation
        active="monitor"
        onMonitor={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onMatches={() => navigate('/match-request')}
        onScan={() => navigate('/profile')}
        onReward={() => navigate('/rewards')}
        onProfile={() => navigate('/profile')}
      />

    </main>

  );

}


export default Monitor;
